/**
 * 魔都市住民電子掲示板 — mato-bbs
 * post_no 連番（昇順採番・最新が最大）+ >>N アンカー返信対応
 */
import React, { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { IMAGES } from "@/assets/images";

/* ─────────────────────────────────────────
   型定義
───────────────────────────────────────── */
interface Post {
  id: string;
  post_no: number;
  author: string;
  message: string;
  message_translated?: string | null;
  reply_to: number | null;
  created_at: string;
  is_bot?: boolean;
}

/* ─────────────────────────────────────────
   定数
───────────────────────────────────────── */
const YEAR_OFFSET_MS = 200 * 365.25 * 24 * 60 * 60 * 1000;

const TEMPLATES = [
  "くずねこ目撃情報：川口駅付近で黒いセーターの猫耳女性を確認しました。",
  "龍族出現注意：本日○○付近上空に龍族の飛行を確認。市民の皆さまはご注意ください。",
  "イカ女さんが公園ベンチで昼寝していました。穏やかでした。",
  "特殊部隊がまた雑居ビルに突入してます。また今日も騒がしい夜になりそう。",
  "市長の演説、今年も感動しました。市民として誇りに思います。",
];

const PLACEHOLDER_NAMES = [
  "名無し市民", "通りすがりの獣人", "川口在住", "魔都市民A",
  "匿名の龍族", "元・特殊部隊員", "イカ女ファン", "くずねこウォッチャー",
];

function randomCitizenNo() {
  return `市民No.${String(Math.floor(Math.random() * 9000) + 1000)}`;
}

function toMatoDate(isoStr: string): string {
  const d = new Date(new Date(isoStr).getTime() + YEAR_OFFSET_MS);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}/${m}/${day} ${hh}:${mm}`;
}

/* ─────────────────────────────────────────
   >>N アンカーリンクのパース
   メッセージ内の >>数字 をリンクに変換
───────────────────────────────────────── */
function parseAnchors(
  text: string,
  postMap: Map<number, Post>,
  onAnchorClick: (no: number) => void
): React.ReactNode[] {
  const parts = text.split(/(>>\d+)/g);
  return parts.map((part, i) => {
    const m = part.match(/^>>(\d+)$/);
    if (m) {
      const no = parseInt(m[1], 10);
      const exists = postMap.has(no);
      return (
        <button
          key={i}
          onClick={() => exists && onAnchorClick(no)}
          className={
            exists
              ? "text-[#3333cc] hover:text-[#5555ff] underline font-bold cursor-pointer"
              : "text-[#9999cc] font-bold cursor-default"
          }
          title={exists ? `>>>${no} へジャンプ` : "該当レスなし"}
        >
          {part}
        </button>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

/* ─────────────────────────────────────────
   IPハッシュ（スパム対策・個人特定不可）
───────────────────────────────────────── */
async function getIpHash(): Promise<string> {
  try {
    const res = await fetch("https://api.ipify.org?format=json");
    const { ip } = await res.json();
    const buf = await crypto.subtle.digest(
      "SHA-256",
      new TextEncoder().encode(ip + "mato-salt-2226")
    );
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, "0"))
      .join("")
      .slice(0, 16);
  } catch {
    return Math.random().toString(36).slice(2, 18);
  }
}

/* ─────────────────────────────────────────
   ブラウザクロムラッパー
───────────────────────────────────────── */
function BrowserChrome({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#1e1e1e] flex flex-col">
      {/* タブバー */}
      <div className="bg-[#2b2b2b] px-3 pt-2 flex items-end gap-1 border-b border-[#111]">
        <div className="flex items-center gap-2 bg-[#f0f0f0] text-[#222] text-xs px-3 py-1.5 rounded-t-md min-w-[140px] max-w-[260px] shrink-0">
          <svg className="w-3 h-3 shrink-0 text-[#1a3a5c]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <circle cx="12" cy="12" r="10" /><path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" /><path d="M2 12h20" />
          </svg>
          <span className="truncate font-medium">住民掲示板 | 魔都市政府</span>
        </div>
        <a href="#/mato-city-gov"
          className="flex items-center gap-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#bbb] hover:text-white text-xs px-3 py-1.5 rounded-t-md transition-colors duration-150 shrink-0">
          <span className="w-3 h-3 shrink-0 rounded-sm bg-white/20 flex items-center justify-center text-[8px] font-black text-white">魔</span>
          <span className="truncate">魔都市政府</span>
        </a>
        <a href="#/"
          className="flex items-center gap-2 bg-[#3a3a3a] hover:bg-[#4a4a4a] text-[#bbb] hover:text-white text-xs px-3 py-1.5 rounded-t-md transition-colors duration-150 shrink-0">
          <span className="w-3 h-3 shrink-0 rounded-sm bg-white/20 flex items-center justify-center text-[8px] font-black text-white">P</span>
          <span className="truncate">P.STUDIO</span>
        </a>
        <button className="w-6 h-6 flex items-center justify-center text-[#777] text-base rounded mb-0.5" disabled>+</button>
      </div>
      {/* アドレスバー */}
      <div className="bg-[#2b2b2b] px-3 py-1.5 flex items-center gap-2 border-b border-[#111]">
        <div className="flex gap-1">
          <button className="w-6 h-6 flex items-center justify-center text-[#999] rounded">‹</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] rounded">›</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] rounded">↻</button>
        </div>
        <div className="flex-1 bg-[#1a1a1a] border border-[#444] rounded-full px-4 py-1 flex items-center gap-2">
          <svg className="w-3 h-3 text-green-400 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <span className="text-[#ccc] text-xs font-mono truncate">
            www.demoncity.go.jp/citizens/bbs/board
          </span>
        </div>
        <div className="flex gap-1">
          <button className="w-6 h-6 flex items-center justify-center text-[#999] rounded">☆</button>
          <button className="w-6 h-6 flex items-center justify-center text-[#999] rounded">⋮</button>
        </div>
      </div>
      {/* コンテンツ */}
      <div className="flex-1 overflow-auto bg-[#e8e8e8]">
        {children}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   管理者削除モーダル
───────────────────────────────────────── */
function AdminDeleteModal({
  postId,
  onClose,
  onDeleted,
}: {
  postId: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  async function handleDelete() {
    if (!pw) return;
    setLoading(true);
    setErr("");
    try {
      const { data, error } = await supabase.rpc("admin_delete_post", {
        p_post_id: postId,
        p_password: pw,
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (result?.success) {
        onDeleted();
        onClose();
      } else {
        setErr(result?.error || "削除に失敗しました");
      }
    } catch {
      setErr("エラーが発生しました");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.7)" }}
      onClick={onClose}>
      <div className="bg-[#1a1a2e] border border-[#3a3a6a] rounded-lg p-6 w-80 shadow-2xl"
        onClick={e => e.stopPropagation()}>
        <p className="text-white/90 text-sm font-bold mb-1">🔐 管理者削除</p>
        <p className="text-white/40 text-xs mb-4">管理者パスワードを入力してください</p>
        <input
          type="password"
          value={pw}
          onChange={e => setPw(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleDelete()}
          placeholder="パスワード"
          className="w-full bg-[#0d0d1a] border border-[#3a3a6a] rounded px-3 py-2 text-white text-sm mb-3 outline-none focus:border-[#6a6aaa]"
          autoFocus
        />
        {err && <p className="text-red-400 text-xs mb-3">{err}</p>}
        <div className="flex gap-2">
          <button onClick={handleDelete} disabled={loading}
            className="flex-1 bg-red-700 hover:bg-red-600 text-white text-xs py-2 rounded transition-colors disabled:opacity-50">
            {loading ? "削除中…" : "削除する"}
          </button>
          <button onClick={onClose}
            className="flex-1 bg-[#2a2a4a] hover:bg-[#3a3a5a] text-white/70 text-xs py-2 rounded transition-colors">
            キャンセル
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   引用プレビュー（>>N の元レス小表示）
───────────────────────────────────────── */
function QuotePreview({
  quotedPost,
  onJump,
}: {
  quotedPost: Post;
  onJump: (no: number) => void;
}) {
  const preview = quotedPost.message.length > 80
    ? quotedPost.message.slice(0, 80) + "…"
    : quotedPost.message;

  return (
    <button
      onClick={() => onJump(quotedPost.post_no)}
      className="w-full text-left mb-2.5 block group"
      title={`>>>${quotedPost.post_no} へジャンプ`}
    >
      <div className="border-l-2 border-[#8888cc] bg-[#f0f0fa] hover:bg-[#e8e8f8] rounded-r px-2.5 py-1.5 transition-colors">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-[9px] font-mono font-bold text-[#5555aa]">&gt;&gt;{quotedPost.post_no}</span>
          <span className="text-[9px] text-[#7777aa] font-bold truncate">{quotedPost.author}</span>
          <span className="text-[8px] text-[#aaaacc] font-mono ml-auto shrink-0">{toMatoDate(quotedPost.created_at)}</span>
        </div>
        <p className="text-[10px] text-[#444466] leading-snug break-words whitespace-pre-wrap line-clamp-3 group-hover:text-[#222244] transition-colors">
          {preview}
        </p>
      </div>
    </button>
  );
}

/* ─────────────────────────────────────────
   投稿カード
───────────────────────────────────────── */
function PostCard({
  post,
  postMap,
  onDeleteRequest,
  onReply,
  onAnchorJump,
}: {
  post: Post;
  postMap: Map<number, Post>;
  onDeleteRequest: (id: string) => void;
  onReply: (no: number) => void;
  onAnchorJump: (no: number) => void;
}) {
  const [showAdmin, setShowAdmin] = useState(false);
  const [highlight, setHighlight] = useState(false);

  // ハイライトアニメーション用（アンカーで飛んできた時）
  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === post.post_no) {
        setHighlight(true);
        setTimeout(() => setHighlight(false), 1800);
      }
    };
    window.addEventListener("bbs-highlight" as keyof WindowEventMap, handler as EventListener);
    return () => window.removeEventListener("bbs-highlight" as keyof WindowEventMap, handler as EventListener);
  }, [post.post_no]);

  return (
    <div
      id={`post-${post.post_no}`}
      className={`border rounded shadow-sm transition-all duration-300 ${
        highlight
          ? "border-[#6666ff] shadow-[0_0_0_2px_rgba(100,100,255,0.3)] bg-[#f0f0ff]"
          : post.is_bot
          ? "border-[#c0a840] hover:shadow-md bg-[#fffdf0]"
          : "border-[#c8c8d8] hover:shadow-md bg-white"
      }`}
    >
      {/* ヘッダー行 */}
      <div className={`flex items-center justify-between px-3 py-2 border-b ${
        post.is_bot
          ? "border-[#e6d080] bg-[#fff8d0]"
          : "border-[#e0e0ee] bg-[#f5f5ff]"
      }`}>
        <div className="flex items-center gap-2 min-w-0">
          {/* 投稿番号 */}
          <span className="text-[11px] font-mono font-bold text-[#3333aa] bg-[#e0e0f8] px-2 py-0.5 rounded shrink-0">
            {post.post_no}
          </span>
          {/* 名前 */}
          <span className={`text-xs font-bold truncate ${
            post.is_bot ? "text-[#7a5a00]" : "text-[#2a2a6a]"
          }`}>{post.author}</span>
          {/* botバッジ */}
          {post.is_bot && (
            <span className="text-[9px] font-bold bg-[#f0c020] text-[#6a4a00] px-1.5 py-0.5 rounded-full shrink-0">✦ 常連</span>
          )}
          {/* 返信先バッジ */}
          {post.reply_to !== null && (
            <button
              onClick={() => onAnchorJump(post.reply_to!)}
              className="text-[10px] text-[#5555bb] hover:text-[#3333ff] bg-[#e8e8ff] hover:bg-[#d8d8ff] border border-[#c0c0ee] px-1.5 py-0.5 rounded transition-colors shrink-0"
              title={`>>>${post.reply_to} へジャンプ`}
            >
              ↩ &gt;&gt;{post.reply_to}
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* 日時 */}
          <span className="text-[10px] text-[#9999bb] font-mono hidden sm:block">
            {toMatoDate(post.created_at)}
          </span>
          {/* 返信ボタン */}
          <button
            onClick={() => onReply(post.post_no)}
            className="text-[10px] text-[#5555aa] hover:text-[#3333cc] bg-[#eeeeff] hover:bg-[#ddddff] border border-[#ccccee] px-2 py-0.5 rounded transition-colors font-bold"
            title={`>>>${post.post_no} に返信`}
          >
            返信
          </button>
          {/* 管理者削除ボタン */}
          <button
            onClick={() => setShowAdmin(true)}
            className="text-[10px] text-[#ccccdd] hover:text-red-400 transition-colors px-1"
            title="管理者削除"
          >
            ×
          </button>
        </div>
      </div>
      {/* 本文 */}
      <div className="px-4 py-3">
        {/* モバイル用日時 */}
        <p className="text-[9px] text-[#aaaacc] font-mono mb-1.5 sm:hidden">
          {toMatoDate(post.created_at)}
        </p>
        {/* 引用プレビュー */}
        {post.reply_to !== null && postMap.has(post.reply_to) && (
          <QuotePreview
            quotedPost={postMap.get(post.reply_to)!}
            onJump={onAnchorJump}
          />
        )}
        <p className="text-sm text-[#222233] whitespace-pre-wrap leading-relaxed break-words">
          {parseAnchors(post.message, postMap, onAnchorJump)}
        </p>
        {/* 翻訳表示 */}
        {post.message_translated && (
          <div className="mt-2 pt-2 border-t border-dashed border-[#d0d0e8]">
            <p className="text-[11px] text-[#6666aa] leading-relaxed break-words">
              <span className="inline-block bg-[#eeeeff] border border-[#ccccee] text-[#5555aa] text-[8px] font-bold px-1.5 py-0.5 rounded mr-1.5 align-middle">
                {/[\u3040-\u30ff\u4e00-\u9faf]/.test(post.message) ? "EN" : "JP"}
              </span>
              {post.message_translated}
            </p>
          </div>
        )}
      </div>
      {showAdmin && (
        <AdminDeleteModal
          postId={post.id}
          onClose={() => setShowAdmin(false)}
          onDeleted={() => onDeleteRequest(post.id)}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   メインページ
───────────────────────────────────────── */
const BBS_AUTHOR_KEY = "mato_bbs_author";
function getSavedAuthor(): string {
  try { return localStorage.getItem(BBS_AUTHOR_KEY) || ""; } catch { return ""; }
}
function saveAuthor(name: string) {
  try { localStorage.setItem(BBS_AUTHOR_KEY, name); } catch { /* ignore */ }
}

export default function MatoBBS() {
  const [posts, setPosts]             = useState<Post[]>([]);
  const [postMap, setPostMap]         = useState<Map<number, Post>>(new Map());
  const [loading, setLoading]         = useState(true);
  const [author, setAuthor]           = useState<string>(getSavedAuthor);
  const [message, setMessage]         = useState("");
  const [replyTo, setReplyTo]         = useState<number | null>(null);
  const [posting, setPosting]         = useState(false);
  const [postError, setPostError]     = useState("");
  const [postSuccess, setPostSuccess] = useState(false);
  const [cooldown, setCooldown]       = useState(0);
  const [ipHash, setIpHash]           = useState("");
  const [page, setPage]               = useState(0);
  const [hasMore, setHasMore]         = useState(true);
  const [botPosting, setBotPosting]   = useState(false);
  const [botMsg, setBotMsg]           = useState("");
  const cooldownRef   = useRef<ReturnType<typeof setInterval> | null>(null);
  const formRef       = useRef<HTMLDivElement>(null);
  const textareaRef   = useRef<HTMLTextAreaElement>(null);
  const PAGE_SIZE = 20;

  // IPハッシュ取得
  useEffect(() => {
    getIpHash().then(setIpHash);
  }, []);

  // postMapを更新するユーティリティ
  function buildMap(list: Post[]): Map<number, Post> {
    const m = new Map<number, Post>();
    list.forEach(p => m.set(p.post_no, p));
    return m;
  }

  // 投稿取得（post_no 降順 → 最新が上）
  const fetchPosts = useCallback(async (pageNum = 0, append = false) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("bbs_posts")
      .select("id, post_no, author, message, message_translated, reply_to, created_at, is_bot")
      .eq("is_deleted", false)
      .order("post_no", { ascending: false })
      .range(pageNum * PAGE_SIZE, (pageNum + 1) * PAGE_SIZE - 1);

    if (!error && data) {
      const typed = data as Post[];
      setPosts(prev => {
        const next = append ? [...prev, ...typed] : typed;
        setPostMap(buildMap(next));
        return next;
      });
      setHasMore(data.length === PAGE_SIZE);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts(0);
  }, [fetchPosts]);

  // リアルタイム購読
  useEffect(() => {
    const channel = supabase
      .channel("bbs_realtime")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "bbs_posts" },
        (payload) => {
          const newPost = payload.new as Post;
          setPosts(prev => {
            const next = [newPost, ...prev];
            setPostMap(buildMap(next));
            return next;
          });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, []);

  // クールダウンタイマー
  useEffect(() => {
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, []);

  function startCooldown(seconds: number) {
    setCooldown(seconds);
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    cooldownRef.current = setInterval(() => {
      setCooldown(c => {
        if (c <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }

  // 返信ボタン押下 → フォームに >>N を挿入してフォーカス
  function handleReply(no: number) {
    setReplyTo(no);
    const anchor = `>>${no} `;
    setMessage(prev => {
      // 既に >>no が先頭にある場合は置換、なければ先頭に付加
      const hasAnchor = prev.startsWith(`>>${no} `);
      return hasAnchor ? prev : anchor + prev;
    });
    // フォームへスクロール
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => textareaRef.current?.focus(), 400);
  }

  // アンカー >>N クリック → 対象にジャンプ＋ハイライト
  function handleAnchorJump(no: number) {
    const el = document.getElementById(`post-${no}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      window.dispatchEvent(new CustomEvent("bbs-highlight", { detail: no }));
    }
  }

  // 禁止名・PPパスワード名
  const BANNED_NAMES: string[]              = ["くずねこ", "ネコマタ", "狼男"];
  const PP_NAME_MAP: Record<string, string> = {
    "くずねこPP": "くずねこ",
    "ネコマタPP": "ネコマタ",
    "狼男PP":    "狼男",
  };

  async function handlePost(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || posting || cooldown > 0) return;

    const trimmedMessage  = message.trim();
    const rawAuthor       = author.trim() || randomCitizenNo();
    const resolvedAuthor  = PP_NAME_MAP[rawAuthor] ?? rawAuthor;

    if (BANNED_NAMES.includes(rawAuthor)) {
      alert(`「${rawAuthor}」の名前での投稿は禁止されています。`);
      return;
    }
    if (trimmedMessage.length > 500) {
      setPostError("500文字以内で入力してください");
      return;
    }

    // メッセージ内 >>N から reply_to を解析
    const anchorMatch = trimmedMessage.match(/^>>(\d+)/);
    const parsedReplyTo = anchorMatch ? parseInt(anchorMatch[1], 10) : null;
    // postMap にない番号なら無視
    const validReplyTo = (parsedReplyTo !== null && postMap.has(parsedReplyTo))
      ? parsedReplyTo
      : null;

    setPosting(true);
    setPostError("");
    setPostSuccess(false);

    try {
      if (ipHash) {
        const { data: allowed } = await supabase.rpc("check_bbs_rate_limit", {
          p_ip_hash: ipHash,
        });
        if (!allowed) {
          setPostError("短時間に投稿が多すぎます。少し待ってからお試しください。");
          setPosting(false);
          return;
        }
      }

      const { data: insertData, error } = await supabase
        .from("bbs_posts")
        .insert({
          author:   resolvedAuthor,
          message:  trimmedMessage,
          ip_hash:  ipHash || null,
          reply_to: validReplyTo,
        })
        .select("id, post_no, author, message, reply_to, created_at")
        .single();

      if (error) throw error;

      if (insertData) {
        supabase.functions.invoke("bbs-notify-email", {
          body: {
            author:     insertData.author,
            message:    insertData.message,
            created_at: insertData.created_at,
          },
        }).catch(() => { /* 通知失敗は無視 */ });
      }

      setMessage("");
      setAuthor("");
      setReplyTo(null);
      setPostSuccess(true);
      startCooldown(30);
      setTimeout(() => setPostSuccess(false), 3000);
    } catch {
      setPostError("投稿に失敗しました。しばらく後にお試しください。");
    } finally {
      setPosting(false);
    }
  }

  function handleDeleted(postId: string) {
    setPosts(prev => {
      const next = prev.filter(p => p.id !== postId);
      setPostMap(buildMap(next));
      return next;
    });
  }

  function loadMore() {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchPosts(nextPage, true);
  }

  // 返信クリアボタン
  function clearReply() {
    setReplyTo(null);
    setMessage(prev => prev.replace(/^>>\d+ ?/, ""));
  }

  return (
    <BrowserChrome>
      <div className="min-h-screen" style={{ background: "#e8e8e8" }}>

        {/* ── ヘッダー ── */}
        <div className="relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, #0a0a2e 0%, #1a1a4e 50%, #0d0d3a 100%)" }}>
          <div className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: "linear-gradient(rgba(100,100,255,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(100,100,255,0.3) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="relative z-10 px-6 py-8 text-center">
            <div className="relative inline-block mb-5">
              <div className="absolute inset-0 rounded-full border-2 border-[#6a6aff]/40 animate-pulse" />
              <div className="w-24 h-24 rounded-full border-2 border-[#6a6aff]/70 overflow-hidden shadow-[0_0_24px_rgba(100,100,255,0.4)]"
                style={{ background: "rgba(10,10,40,0.85)" }}>
                <img src={IMAGES.CITY_EMBLEM_NEW} alt="魔都市章"
                  className="w-full h-full object-contain p-1.5 opacity-90" />
              </div>
              <span className="absolute -bottom-1 -right-1 bg-[#3a3aaa] border border-[#6a6aff]/60 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full shadow-lg tracking-wider">BBS</span>
            </div>
            <h1 className="text-white font-bold text-xl mb-1 tracking-widest">
              魔都市住民電子掲示板
            </h1>
            <p className="text-[#8888cc] text-xs tracking-wider mb-1">
              DEMON CITY CITIZENS' BULLETIN BOARD SYSTEM
            </p>
            <p className="text-[#5555aa] text-[10px]">
              魔都市政府公認 ／ 運営：市民サービス局 デジタル推進課
            </p>
            <div className="mt-4 mx-auto max-w-xl bg-[#1a0a0a]/60 border border-red-900/40 rounded px-4 py-2 text-[10px] text-[#cc8888] text-left">
              ⚠️ 個人情報・誹謗中傷・魔族機密情報の書き込みは禁止です。
              違反投稿は魔都市条例第42条に基づき削除されます。
            </div>
          </div>
        </div>

        <div className="max-w-2xl mx-auto px-4 py-6">

          {/* ── 投稿フォーム ── */}
          <div ref={formRef} className="bg-white border border-[#c8c8d8] rounded shadow-sm mb-6 overflow-hidden">
            <div className="bg-[#f0f0ff] border-b border-[#d0d0ee] px-4 py-2 flex items-center gap-2">
              <span className="text-[#4444aa] text-xs font-bold tracking-wider">✏️ 新規投稿</span>
              {replyTo !== null && (
                <div className="flex items-center gap-1.5 ml-auto">
                  <span className="text-[10px] text-[#5555aa] bg-[#e8e8ff] border border-[#ccccee] px-2 py-0.5 rounded font-bold">
                    ↩ &gt;&gt;{replyTo} に返信中
                  </span>
                  <button
                    onClick={clearReply}
                    className="text-[10px] text-[#aa5555] hover:text-red-600 bg-[#ffeeee] border border-[#eecccc] px-1.5 py-0.5 rounded transition-colors"
                    title="返信をキャンセル"
                  >
                    クリア
                  </button>
                </div>
              )}
            </div>
            <form onSubmit={handlePost} className="p-4 space-y-3">
              {/* 名前 */}
              <div className="flex items-center gap-2">
                <label className="text-[10px] text-[#6666aa] w-12 shrink-0 tracking-wider">名前</label>
                <input
                  type="text"
                  value={author}
                  onChange={e => { setAuthor(e.target.value); saveAuthor(e.target.value); }}
                  maxLength={30}
                  placeholder={PLACEHOLDER_NAMES[Math.floor(Math.random() * PLACEHOLDER_NAMES.length)]}
                  className="flex-1 bg-[#f8f8ff] border border-[#c8c8e8] rounded px-3 py-1.5 text-sm text-[#111] placeholder:text-[#aaa] focus:outline-none focus:border-[#8888cc] transition-colors"
                />
              </div>

              {/* テンプレートボタン */}
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATES.map((t, i) => (
                  <button key={i} type="button" onClick={() => setMessage(t)}
                    className="text-[9px] bg-[#eeeeFF] hover:bg-[#ddddff] border border-[#ccccee] text-[#5555aa] px-2 py-0.5 rounded transition-colors">
                    テンプレ{i + 1}
                  </button>
                ))}
                <button type="button" onClick={() => { setMessage(""); clearReply(); }}
                  className="text-[9px] bg-[#ffeeee] hover:bg-[#ffdddd] border border-[#eecccc] text-[#aa5555] px-2 py-0.5 rounded transition-colors">
                  クリア
                </button>
              </div>

              {/* メッセージ */}
              <div>
                <textarea
                  ref={textareaRef}
                  value={message}
                  onChange={e => {
                    setMessage(e.target.value);
                    // >>N がテキストから消えたら replyTo もリセット
                    if (replyTo !== null && !e.target.value.startsWith(`>>${replyTo}`)) {
                      setReplyTo(null);
                    }
                  }}
                  required
                  rows={4}
                  maxLength={500}
                  placeholder={replyTo !== null ? `>>${replyTo} への返信を書く… (500文字まで)` : "目撃情報・ご意見・雑談など何でも。（500文字まで）"}
                  className="w-full bg-[#f8f8ff] border border-[#c8c8e8] rounded px-3 py-2 text-sm text-[#111] placeholder:text-[#bbb] focus:outline-none focus:border-[#8888cc] transition-colors resize-none"
                />
                <div className="flex justify-between items-center mt-0.5">
                  <span className="text-[9px] text-[#aaaacc]">
                    ヒント: 本文内に <strong>&gt;&gt;番号</strong> を書くと返信アンカーになります
                  </span>
                  <span className={`text-[10px] ${message.length > 450 ? "text-red-400" : "text-[#9999bb]"}`}>
                    {message.length}/500
                  </span>
                </div>
              </div>

              {/* エラー・成功 */}
              {postError && (
                <p className="text-xs text-red-500 bg-red-50 border border-red-200 rounded px-3 py-2">{postError}</p>
              )}
              {postSuccess && (
                <p className="text-xs text-green-600 bg-green-50 border border-green-200 rounded px-3 py-2">✅ 投稿しました！</p>
              )}

              {/* 送信 */}
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-[#9999bb]">
                  ※名前を空欄にすると自動で市民番号が割り当てられます
                </p>
                <button
                  type="submit"
                  disabled={posting || cooldown > 0 || !message.trim()}
                  className="bg-[#3333aa] hover:bg-[#4444cc] disabled:bg-[#9999cc] text-white text-xs px-5 py-2 rounded transition-colors disabled:cursor-not-allowed font-bold tracking-wider"
                >
                  {posting ? "送信中…" : cooldown > 0 ? `待機中 (${cooldown}s)` : "書き込む"}
                </button>
              </div>
            </form>
          </div>

          {/* ── 投稿一覧ヘッダー ── */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-[#4444aa] text-xs font-bold tracking-wider">📜 投稿一覧</span>
              <span className="text-[10px] text-[#9999bb] bg-white border border-[#d0d0ee] px-2 py-0.5 rounded">
                {posts.length}件表示
              </span>
              {posts.length > 0 && (
                <span className="text-[10px] text-[#7777aa] bg-[#eeeeff] border border-[#d0d0ee] px-2 py-0.5 rounded font-mono">
                  最新: #{posts[0]?.post_no}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* 名物投稿者 招待ボタン */}
              <button
                onClick={async () => {
                  if (botPosting) return;
                  setBotPosting(true);
                  setBotMsg("");
                  try {
                    const { data, error } = await supabase.functions.invoke("mato-bbs-bot-2026-04-20", {
                      body: { count: 1 },
                    });
                    if (error) {
                      console.error("Bot invoke error:", error);
                      setBotMsg(`⚠ botエラー: ${error.message || JSON.stringify(error)}`);
                      setTimeout(() => setBotMsg(""), 5000);
                    } else {
                      const res = data as { results?: Array<{success: boolean}> };
                      const ok = res?.results?.[0]?.success;
                      if (ok) {
                        setBotMsg("✦ 名物投稿者が書き込みました！");
                        setTimeout(() => { setPage(0); fetchPosts(0); }, 800);
                        setTimeout(() => setBotMsg(""), 4000);
                      } else {
                        setBotMsg(`⚠ bot投稿失敗: ${JSON.stringify(res)}`);
                        setTimeout(() => setBotMsg(""), 5000);
                      }
                    }
                  } catch (e: unknown) {
                    const msg = e instanceof Error ? e.message : String(e);
                    console.error("Bot catch:", e);
                    setBotMsg(`⚠ 例外: ${msg}`);
                    setTimeout(() => setBotMsg(""), 5000);
                  } finally {
                    setBotPosting(false);
                  }
                }}
                disabled={botPosting}
                className="text-[10px] text-[#8855aa] hover:text-[#5522aa] bg-[#f8f0ff] border border-[#d8c0ee] hover:border-[#a888cc] px-3 py-1 rounded transition-colors disabled:opacity-50"
                title="名物投稿者を呼ぶ（botが1件書き込みます）"
              >
                {botPosting ? "招待中…" : "✦ 名物投稿者を呼ぶ"}
              </button>
              <button
                onClick={() => { setPage(0); fetchPosts(0); }}
                className="text-[10px] text-[#6666aa] hover:text-[#3333aa] bg-white border border-[#c8c8e8] hover:border-[#9999cc] px-3 py-1 rounded transition-colors"
              >
                ↻ 更新
              </button>
            </div>
          </div>

          {/* botメッセージ */}
          {botMsg && (
            <div className="text-center text-[11px] text-[#8855aa] bg-[#f8f0ff] border border-[#d8c0ee] rounded px-3 py-2 mb-3 animate-pulse">
              {botMsg}
            </div>
          )}

          {/* ── 投稿リスト ── */}
          {loading && posts.length === 0 ? (
            <div className="text-center py-16 text-[#8888aa]">
              <div className="text-3xl mb-3">📡</div>
              <p className="text-sm">サーバーに接続中…</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-[#8888aa]">
              <div className="text-3xl mb-3">🌙</div>
              <p className="text-sm">まだ投稿がありません。最初の市民になりましょう。</p>
            </div>
          ) : (
            <div className="space-y-3">
              {posts.map(post => (
                <PostCard
                  key={post.id}
                  post={post}
                  postMap={postMap}
                  onDeleteRequest={handleDeleted}
                  onReply={handleReply}
                  onAnchorJump={handleAnchorJump}
                />
              ))}
            </div>
          )}

          {/* もっと読む */}
          {hasMore && !loading && (
            <div className="text-center mt-6">
              <button
                onClick={loadMore}
                className="text-xs text-[#6666aa] hover:text-[#3333aa] bg-white border border-[#c8c8e8] hover:border-[#9999cc] px-6 py-2 rounded transition-colors"
              >
                さらに読み込む
              </button>
            </div>
          )}

          {loading && posts.length > 0 && (
            <div className="text-center mt-4 text-[10px] text-[#9999bb]">読み込み中…</div>
          )}

          {/* フッター */}
          <div className="mt-12 text-center border-t border-[#c8c8d8] pt-6 pb-4">
            <p className="text-[10px] text-[#9999bb]">魔都市政府 市民サービス局 デジタル推進課</p>
            <p className="text-[10px] text-[#9999bb]">Tel: 0570-XXX-XXXX（魔都市内専用回線）</p>
            <p className="text-[9px] text-[#cccccc] mt-2">© 2226 Demon City Government. All Rights Reserved.</p>
            <a href="#/admin"
              className="inline-block mt-4 text-[9px] text-[#cccccc]/30 hover:text-[#9999bb] transition-colors tracking-widest">
              ▲ 管理
            </a>
          </div>
        </div>
      </div>
    </BrowserChrome>
  );
}
